// @flow
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import { saveReactionSummary } from './actions';
import {
  GET_REACTION_SUMMARY,
  ADD_REACTION_TO_SUMMARY,
  REMOVE_REACTION_FROM_SUMMARY,
} from './queries';

type Props = {
  data: *,
  token: string,
  chatUuid: string,
  children: Function,
  messageUuid: string,
  addReactionToSummary: Function,
  removeReactionFromSummary: Function,
};

class ReactionContainer extends React.PureComponent<Props, void> {
  addReactionToSummary = (name: string) => {
    this.props.addReactionToSummary(name);
  };

  removeReactionFromSummary = (name: string) => {
    this.props.removeReactionFromSummary(name);
  };

  render() {
    if (!this.props.data) {
      return null;
    }

    // I've only seen this in dev and not in production but:
    //
    // There is some weird ghost result: { Symbol(id): "ReactionSummary_{SOME_MESSAGE_UUID}" }
    // I'm not sure why the HOC sometimes returns a near empty object and sometimes not.
    // Those ghost results are not stored in the cache.
    //
    // When receiving such object treat it a null object.
    let { reactionSummary } = this.props.data;
    if (reactionSummary && Object.keys(reactionSummary).length <= 1) {
      reactionSummary = null;
    }

    return this.props.children({
      reactionSummary,
      addReactionToSummary: this.addReactionToSummary,
      removeReactionFromSummary: this.removeReactionFromSummary,
    });
  }
}

const withAddReactionToSummary = graphql(ADD_REACTION_TO_SUMMARY, {
  props: ({ ownProps, mutate }) => ({
    addReactionToSummary: name => {
      return mutate({
        variables: {
          name,
          chatUuid: ownProps.chatUuid,
          messageUuid: ownProps.messageUuid,
          chatMessageReactionToken: ownProps.token,
        },
        update: (proxy, { data }) => saveReactionSummary(data.addReaction.reactionSummary),
      });
    },
  }),
});

const withRemoveReactionFromSummary = graphql(REMOVE_REACTION_FROM_SUMMARY, {
  props: ({ ownProps, mutate }) => ({
    removeReactionFromSummary: name => {
      return mutate({
        variables: {
          name,
          chatUuid: ownProps.chatUuid,
          messageUuid: ownProps.messageUuid,
          chatMessageReactionToken: ownProps.token,
        },
        update: (proxy, { data }) => saveReactionSummary(data.removeReaction.reactionSummary),
      });
    },
  }),
});

const withReaction = graphql(GET_REACTION_SUMMARY, {
  options: ({ messageUuid }) => ({
    fetchPolicy: 'cache-only',
    variables: { messageUuid },
  }),
});

export { ReactionContainer };

export default compose(
  withReaction,
  withRemoveReactionFromSummary,
  withAddReactionToSummary
)(ReactionContainer);
